import { registerLocaleData } from '@angular/common';
import { Component } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import localeEn from '@angular/common/locales/en-CA';
import localeFr from '@angular/common/locales/fr-CA';
import { AppConstants } from 'src/utils/app.constants';
import { LocalStorage } from 'src/utils/local-storage';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { HubService } from 'src/services/hub.service';
import { TokenTransferService } from 'src/services/token-transfer-service';
import { NavigationEnd, Router } from '@angular/router';
import { AuthService } from 'src/services/auth.service';
import { UserProfile } from 'src/services/auth/userProfile';
import { filter } from 'rxjs/operators';
import { JwtTokenService } from 'src/services/jwt-token.service';
import { DatalayerService } from 'src/services/datalayer.service';
import { CpsNavigationService } from 'src/services/cps-navigation.service';


@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent {
  title = 'cpha-hub';
  isLoginPage = false;
  isInitializing = true;
  lastTimeResize = 0;

  public userProfile: UserProfile = null;
  
  constructor(private translateService: TranslateService,
    private localStorage: LocalStorage,
    private tokenTransferService: TokenTransferService,
    private authService: AuthService,
    private router: Router,
    private jwtTokenService: JwtTokenService,
    private datalayer: DatalayerService,
    private navigationService: CpsNavigationService
  ) {

    const thizz = this;

    const isLoggingOut = new URLSearchParams(window.location.search).get('logout') || window.location.href.indexOf('/logout') !== -1;
    if (isLoggingOut) {
      this.navigationService.logout();
    }

    registerLocaleData(localeEn, 'en');
    registerLocaleData(localeFr, 'fr');

    // if the language param is set
    const lang = new URLSearchParams(window.location.search).get('lang');
    let preferredLang = this.localStorage.preferredLang.getValue();
    if (!!lang) {
      preferredLang = lang;
      localStorage.setPreferredLang(lang);
    }

    this.translateService.use(preferredLang);

    if (!LocalStorage.getPreferredLang()) {
      this.translateService.setDefaultLang(AppConstants.ENGLISH_CODE);
      localStorage.setPreferredLang(this.translateService.getDefaultLang());
    }

    if (window.location.href.indexOf('logout') === -1) {

      this.authService.checkToken(this.localStorage.getAccessToken()).subscribe({
        next: () => {
          this.router.navigate(['home']);
        },
        error: () => {
          // No valid token found. Proceed as normal.
        }
      });
    } else {
      const refreshToken = this.localStorage.getRefreshToken();
      const profile = this.localStorage.getUserProfile();
      this.localStorage.clearAll();
      // If we are arriving at the HUB from a CPS 2 logout We should have cleared the session from the CPS2 side. No need to repeat the server logout request 
    }

    this.translateService.use(this.localStorage.preferredLang.getValue());

    this.translateService.onLangChange.subscribe(
      () => {
        window.history.pushState('object', document.title, location.href.split(/[?#]/)[0]);
        // Reload the entire app after a language change, just in case for potential data issues
        // NOTE: Exception for the search results, we put them back to the homepage first
        //       see cps-utility-nav.ts for details
        if (location.href.indexOf('/search/') < 0) {
          this.router.navigate([this.router.url.split('?')[0]]);
          location.reload();
        }
    });

    // this.hubService.getAbsorbAuthToken().subscribe((value) => {
    //    console.log(value);
    // });
    
    const params = new URLSearchParams(window.location.search);

    // Check for refresh token in URL (for example ?r=refreshTokenValue)
    const refreshTokenFromUrl = params.get('r');

    if (refreshTokenFromUrl) {
      this.authService.getRefreshToken(refreshTokenFromUrl).subscribe({
        next: (token) => {
          // Save the new access and refresh token to localStorage and set user profile
          this.redirectSuccessfulLogin(token);
        },
        error: (err) => {
          console.error('Refresh token exchange failed', err);
        }
      });
    }

    setTimeout(() => {
      this.isInitializing = false;
    }, 100);

    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        this.isLoginPage = this.isLoginRoute(this.router.url);
    });

    // Initialize the feedback form
    this.loadJiraFeedbackScript();

    window.addEventListener('resize', (event) => {
      this.throttleResize();
    });
  }

  // Avoid firing this event for every resize tick to reduce performance cost
  private throttleResize() {
    const now = Date.now();
    if (now - this.lastTimeResize >= 1000 && !this.datalayer.getResizing()) {
      this.lastTimeResize = now;
      this.datalayer.resizing(true);

      // Reset the flag after a small period of time has past.
      setTimeout(() => {
        this.datalayer.resizing(false);
      }, 5000);
    }
  }

  private isLoginRoute(url: string): boolean {
    const cleanUrl = url.split('?')[0];
    return cleanUrl === '/' || cleanUrl === '' || cleanUrl === '/login';
  }
  
  redirectSuccessfulLogin(token: any) {
    this.userProfile = new UserProfile();
              
    this.jwtTokenService.setToken(token.access_token);
    this.userProfile = this.jwtTokenService.getUserInfo();
    this.userProfile.access_token = token.access_token;
    this.userProfile.refresh_token = token.refresh_token;
    const updatedAuthorities = this.jwtTokenService.getAuthorities();
    this.userProfile.authorities = updatedAuthorities;

    this.localStorage.setAccessToken(token.access_token);
    this.localStorage.setRefreshToken(token.refresh_token);
    this.localStorage.setUserProfile(this.userProfile);

    this.datalayer.loginSuccess();
  
    const hubAccessCookie = this.userProfile.hub_access;

    this.tokenTransferService.sendTokensToCps(this.userProfile);
    

    if (hubAccessCookie) {
      this.router.navigate(['home']);
    } else {
      this.navigationService.goToCps();
    }
  }

  // The feedback form creation script
  loadJiraFeedbackScript() {
    let node = document.createElement('script');
    node.src = this.translateService.currentLang.toLowerCase() == 'en'
    ? 'https://cpha.atlassian.net/s/d41d8cd98f00b204e9800998ecf8427e-T/tod1zk/b/5/c95134bc67d3a521bb3f4331beb9b804/_/download/batch/com.atlassian.jira.collector.plugin.jira-issue-collector-plugin:issuecollector/com.atlassian.jira.collector.plugin.jira-issue-collector-plugin:issuecollector.js?locale=en-GB&collectorId=e24c1197'
    : 'https://cpha.atlassian.net/s/d41d8cd98f00b204e9800998ecf8427e-T/tod1zk/b/6/c95134bc67d3a521bb3f4331beb9b804/_/download/batch/com.atlassian.jira.collector.plugin.jira-issue-collector-plugin:issuecollector/com.atlassian.jira.collector.plugin.jira-issue-collector-plugin:issuecollector.js?locale=fr-CA&collectorId=ca2e2f9e';
    node.type = 'text/javascript'; // set the script type
    node.async = true; // makes script run asynchronously
    node.charset = 'utf-8';
    // append to head of document
    document.getElementsByTagName('head')[0].appendChild(node);
  }

}
